import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { ACTION } from '@atlaskit/editor-common/analytics';
import { ACTION_SUBJECT_ID, EVENT_TYPE, ACTION_SUBJECT } from '@atlaskit/editor-common/analytics';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';

import type { EditorOfflineStoragePlugin } from './editorOfflineStoragePluginType';
import type { Status } from './pm-plugins/resync-online';

type BaseAnalyticsEventProps = {
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined;
};

type ResyncOnlineEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_RESYNC_ONLINE;
	resyncStatus: Status;
	isActiveTab: boolean;
};

type BroadcastChannelUpdateEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_BROADCAST_CHANNEL_FIRST_UPDATE;
};

type TooManyRetriesErrorEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_TOO_MANY_RETRIES_ERROR;
	errorMessage: string;
	errorInfo: string;
	retrySource: string;
	maxRetries: number;
};

type FailedStepsErrorEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_FAILED_STEPS;
	errorInfo: string;
	stepsCount: number;
};

type StepsDatabaseErrorEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_STEPS_DATABASE_ERROR;
	errorMessage: string;
	errorSource: string;
	errorCause: {
		contentIdPresent: boolean;
		stepsDatabaseInitAttempted: boolean;
		isSSR: boolean;
		isIndexedDBAvailable: boolean | undefined;
	};
};

type FireAnalyticsEventProps =
	| TooManyRetriesErrorEventProps
	| StepsDatabaseErrorEventProps
	| ResyncOnlineEventProps
	| BroadcastChannelUpdateEventProps
	| FailedStepsErrorEventProps;

export const fireOfflineStorageAnalyticsEvent = (props: FireAnalyticsEventProps) => {
	const { api, action, ...otherAttributes } = props;
	if (!api || !api.analytics) {
		return;
	}

	// Get additional context from shared state
	const sharedPluginState = api?.editorOfflineStorage?.sharedState.currentState();
	const { version, steps, status, retryCount, disabled } = sharedPluginState ?? {};

	api.analytics.actions?.fireAnalyticsEvent?.({
		eventType: EVENT_TYPE.OPERATIONAL,
		action,
		actionSubject: ACTION_SUBJECT.OFFLINE_EDITING,
		actionSubjectId: ACTION_SUBJECT_ID.OFFLINE_EDITING,
		attributes: {
			// Additonal context.
			version,
			stepsCount: steps?.length,
			status,
			retryCount,
			disabled,
			// Action specific attributes.
			...otherAttributes,
		},
	});
};

export const parseErrorMessage = (error: Error | unknown) => {
	if (!error) {
		return 'Unknown error';
	}
	if (error instanceof Error) {
		return `${error.name}: ${error.message}`;
	}
	return String(error);
};

export const parseErrorMessages = (errors: Error[]) => {
	return errors.map((e) => parseErrorMessage(e)).join(';');
};
